import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Stack,
  Avatar,
  Button,
} from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { theme } from "../../../../../configs/theme";
import imageFileToBase64 from "../../../../../hooks/imageToBase64";

const ListForm = ({
  listName,
  setListName,
  description,
  setDescription,
  icon,
  setIcon,
  error,
}) => {
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconError, setIconError] = useState("");

  const handleIconChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingIcon(true);
    setIconError("");
    try {
      const base64 = await imageFileToBase64(file, 200);
      setIcon(base64);
    } catch (err) {
      console.error(err);
      setIconError(err.message || "Unable to process image.");
    } finally {
      setUploadingIcon(false);
    }
  };

  return (
    <Box>
      <Stack spacing={2.5}>
        <Box
          sx={{
            width: "100%",
            p: 2,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#fafafa",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            src={icon || undefined}
            alt={listName || "Mailing list"}
            sx={{
              width: 88,
              height: 88,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              color: "text.secondary",
              fontSize: 32,
              fontWeight: 600,
            }}
          >
            {(listName || "M").charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            List icon
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", mt: -0.5 }}
          >
            Optional image shown with this mailing list.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            size="small"
            startIcon={<PhotoCameraOutlinedIcon />}
            disabled={uploadingIcon}
            theme={theme}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
            }}
          >
            {uploadingIcon ? "Processing..." : "Upload icon"}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleIconChange}
            />
          </Button>
          {iconError ? (
            <Typography variant="body2" color="error">
              {iconError}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5, fontWeight: 600 }}
          >
            List name
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            A short name for this mailing list.
          </Typography>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="e.g. Q1 product leads"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5, fontWeight: 600 }}
          >
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            What this list is used for.
          </Typography>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            multiline
            minRows={3}
            placeholder="e.g. Leads interested in freight updates"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        </Box>

        {error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ListForm;
